import { spawnSync } from 'node:child_process';
import { symlinkSync, mkdirSync, statSync, readFileSync, rmSync } from 'node:fs';
import { join, resolve } from 'node:path';

const VALID_SEGMENT = /^[a-zA-Z0-9._-]+$/;
const MAX_SLUG_LENGTH = 64;
const GIT_NO_PROMPT_ENV = { GIT_TERMINAL_PROMPT: '0', GIT_ASKPASS: '' };

export function validateWorktreeSlug(slug: string): void {
  if (!slug) throw new Error('worktree slug must not be empty');
  if (slug.length > MAX_SLUG_LENGTH) throw new Error(`worktree slug must be ≤${MAX_SLUG_LENGTH} chars (got ${slug.length})`);
  for (const seg of slug.split('/')) {
    if (seg === '.' || seg === '..') throw new Error(`worktree slug must not contain "." or ".." segments`);
    if (!VALID_SEGMENT.test(seg)) throw new Error(`worktree slug segment "${seg}" contains invalid characters (allowed: a-z A-Z 0-9 . _ -)`);
  }
}

function flattenSlug(slug: string): string { return slug.replaceAll('/', '+'); }
export function worktreeBranchName(slug: string): string { return `worktree-${flattenSlug(slug)}`; }
function worktreesDir(repoRoot: string): string { return join(repoRoot, '.claude', 'worktrees'); }
function worktreePathFor(repoRoot: string, slug: string): string { return join(worktreesDir(repoRoot), flattenSlug(slug)); }

function git(args: string[], cwd: string): { ok: boolean; stdout: string; stderr: string } {
  const r = spawnSync('git', args, {
    cwd, stdio: ['ignore', 'pipe', 'pipe'],
    env: { ...process.env, ...GIT_NO_PROMPT_ENV },
  });
  return { ok: r.status === 0, stdout: (r.stdout ?? '').toString().trim(), stderr: (r.stderr ?? '').toString().trim() };
}

export interface WorktreeInfo {
  worktreePath: string;
  branch: string;
  headCommit: string;
  existed: boolean;
  baseBranch?: string;
}

export function findGitRoot(from: string): string | null {
  const r = git(['rev-parse', '--show-toplevel'], from);
  // Windows git prints `C:/Users/…` — make it a native path so it compares/joins like every other host path.
  return r.ok ? (process.platform === 'win32' ? resolve(r.stdout) : r.stdout) : null;
}

function readWorktreeHead(worktreePath: string): string | null {
  try {
    const gitFile = readFileSync(join(worktreePath, '.git'), 'utf-8').trim();
    const m = gitFile.match(/^gitdir:\s*(.+)$/);
    if (!m) return null;
    const headPath = join(m[1], 'HEAD');
    const head = readFileSync(headPath, 'utf-8').trim();
    const refMatch = head.match(/^ref:\s*(.+)$/);
    if (!refMatch) return head.length === 40 ? head : null;
    const refPath = join(m[1], '..', refMatch[1]);
    try { return readFileSync(refPath, 'utf-8').trim(); } catch { /* packed ref — fall through to git */ }
    const r = git(['rev-parse', 'HEAD'], worktreePath);
    return r.ok ? r.stdout : null;
  } catch { return null; }
}

export function getOrCreateWorktree(repoRoot: string, slug: string): WorktreeInfo {
  validateWorktreeSlug(slug);
  const worktreePath = worktreePathFor(repoRoot, slug);
  const branch = worktreeBranchName(slug);

  const existingHead = readWorktreeHead(worktreePath);
  if (existingHead) return { worktreePath, branch, headCommit: existingHead, existed: true };

  mkdirSync(worktreesDir(repoRoot), { recursive: true });

  const defaultBranch = git(['symbolic-ref', '--short', 'refs/remotes/origin/HEAD'], repoRoot);
  let baseBranch = defaultBranch.ok ? defaultBranch.stdout.replace(/^origin\//, '') : 'HEAD';

  const originRef = `origin/${baseBranch}`;
  const hasOrigin = git(['rev-parse', '--verify', originRef], repoRoot);
  if (!hasOrigin.ok) {
    const fetched = git(['fetch', 'origin', baseBranch], repoRoot);
    if (!fetched.ok) baseBranch = 'HEAD';
  }
  const base = baseBranch === 'HEAD' ? 'HEAD' : originRef;

  const r = git(['worktree', 'add', '-B', branch, worktreePath, base], repoRoot);
  if (!r.ok) throw new Error(`failed to create worktree: ${r.stderr}`);

  const headSha = git(['rev-parse', 'HEAD'], worktreePath);

  symlinkDirs(repoRoot, worktreePath, ['node_modules', '.bun']);

  return { worktreePath, branch, headCommit: headSha.stdout, existed: false, baseBranch };
}

function symlinkDirs(repoRoot: string, worktreePath: string, dirs: string[]): void {
  for (const dir of dirs) {
    const src = join(repoRoot, dir);
    const dst = join(worktreePath, dir);
    try { statSync(src); } catch { continue; }
    // Windows: a junction — a directory symlink needs Developer Mode/admin there, a junction doesn't.
    try { symlinkSync(src, dst, process.platform === 'win32' ? 'junction' : 'dir'); } catch (e: any) { if (e?.code !== 'EEXIST') console.error(`[worktree] symlink ${dir}: ${e?.message ?? e}`); }
  }
}

export function cleanupWorktree(repoRoot: string, slug: string, opts?: { force?: boolean }): { removed: boolean; reason?: string } {
  validateWorktreeSlug(slug);
  const worktreePath = worktreePathFor(repoRoot, slug);
  const branch = worktreeBranchName(slug);

  try { statSync(worktreePath); } catch { return { removed: false, reason: 'not found' }; }

  if (!opts?.force) {
    const status = git(['status', '--porcelain', '-uno'], worktreePath);
    if (status.ok && status.stdout) return { removed: false, reason: 'uncommitted changes' };
  }

  // Remove symlinks first so git worktree remove doesn't try to recurse into them
  for (const dir of ['node_modules', '.bun']) {
    try { rmSync(join(worktreePath, dir)); } catch { /* ok */ }
  }

  const rm = git(['worktree', 'remove', '--force', worktreePath], repoRoot);
  if (!rm.ok) return { removed: false, reason: rm.stderr };

  git(['branch', '-D', branch], repoRoot);
  return { removed: true };
}

export interface WorktreeSession {
  originalCwd: string;
  worktreePath: string;
  slug: string;
  branch: string;
  headCommit: string;
}

let _session: WorktreeSession | null = null;
export function getWorktreeSession(): WorktreeSession | null { return _session; }

export function enterWorktree(slug: string, cwd: string): WorktreeSession {
  const repoRoot = findGitRoot(cwd);
  if (!repoRoot) throw new Error('--worktree requires a git repository');

  const info = getOrCreateWorktree(repoRoot, slug);
  process.chdir(info.worktreePath);

  _session = {
    originalCwd: cwd,
    worktreePath: info.worktreePath,
    slug,
    branch: info.branch,
    headCommit: info.headCommit,
  };
  return _session;
}

export function exitWorktree(): { removed: boolean; reason?: string } | null {
  if (!_session) return null;
  const repoRoot = findGitRoot(_session.originalCwd);
  if (!repoRoot) return null;

  process.chdir(_session.originalCwd);
  const result = cleanupWorktree(repoRoot, _session.slug);
  _session = null;
  return result;
}
